// src/components/legal/ArrowQuestPrivacyPolicy.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #2E86EE;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #2E86EE;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const CompanyInfo = styled.div`
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    white-space: pre-wrap;
`;

const InfoBox = styled.div`
    background: #E8F1FC;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const ArrowQuestPrivacyPolicy = () => {
    return (
        <Container>
            <BackLink to="/arrowquest">Arrow Quest로 돌아가기</BackLink>

            <Header>
                <Title>Arrow Quest Privacy Policy / 개인정보처리방침</Title>
                <UpdateDate>Last updated / 최종 수정일: May 26, 2026</UpdateDate>
            </Header>

            <CompanyInfo>
                <SectionTitle>Data Controller / 데이터 컨트롤러 정보</SectionTitle>
                <Paragraph>
                    • Company / 회사명: MindWorld (마인드월드)
                    • Representative / 대표자: 윤우재
                    • Address / 주소: 경기도 의왕시 시청1로 11, Republic of Korea
                    • Business Registration / 사업자등록번호: 504-43-28680
                    • Email: mind-wellness@naver.com
                    • Service / 서비스: Arrow Quest mobile game (Android)
                </Paragraph>
            </CompanyInfo>

            <Section>
                <SectionTitle>1. Overview / 개요</SectionTitle>
                <Paragraph>
                    MindWorld ("we", "us", "Company") respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you use Arrow Quest, a casual mobile puzzle game. This policy is drafted to comply with the EU General Data Protection Regulation (GDPR), the UK GDPR, the California Consumer Privacy Act (CCPA/CPRA), the Korean Personal Information Protection Act (PIPA), and the Children's Online Privacy Protection Act (COPPA).

                    마인드월드(이하 "회사")는 사용자의 개인정보 보호를 매우 중요하게 생각합니다. 본 개인정보처리방침은 EU 일반데이터보호규정(GDPR), 영국 GDPR, 캘리포니아 소비자 개인정보보호법(CCPA/CPRA), 한국 개인정보보호법, 미국 아동 온라인 개인정보보호법(COPPA) 등 관련 법규를 준수하여 작성되었습니다.

                    By installing or using Arrow Quest, you acknowledge that you have read and understood this policy.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. Legal Basis for Processing (GDPR) / 처리의 법적 근거</SectionTitle>
                <Paragraph>
                    Under GDPR Article 6, we process personal data on the following legal bases:

                    • Performance of a contract (Art. 6(1)(b)): Providing the game service and processing in-app purchases.
                    • Legitimate interests (Art. 6(1)(f)): Service security, fraud prevention, analytics, and non-personalized advertising. We balance our interests against your rights and freedoms.
                    • Consent (Art. 6(1)(a)): Personalized advertising and analytics requiring tracking identifiers. Consent is obtained via the Google User Messaging Platform (UMP) consent form on first launch in applicable regions (EEA, UK, Switzerland).
                    • Legal obligation (Art. 6(1)(c)): Tax records, fraud reporting, lawful requests.

                    You may withdraw consent at any time through in-game settings (Settings → Privacy). Withdrawal does not affect lawfulness of prior processing.

                    동의 철회: 게임 내 설정(Settings → Privacy)에서 언제든지 동의를 철회하실 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. Information We Collect / 수집하는 정보</SectionTitle>
                <Paragraph>
                    Arrow Quest does not require account registration to play. Some optional features (cloud save, leaderboards) require Firebase Authentication. We collect only the minimum data necessary for the game and the features you choose to use.

                    Automatically collected:
                    • Device identifiers: Android Advertising ID (AAID), Android ID (hashed where possible)
                    • Device information: model, OS version, language, country, screen resolution
                    • IP address (truncated/anonymized for analytics where possible)
                    • Crash logs and performance metrics
                    • Gameplay events: level reached, sessions, in-app purchases, ad impressions
                    • Approximate location (country-level, derived from IP)

                    Firebase Authentication identifier (created automatically on first launch for cloud-save bootstrapping):
                    • Firebase UID (anonymous): a random, app-scoped identifier issued by Firebase Authentication. It is not linked to your name, email, or device until you optionally sign in.
                    • If you tap "Sign in with Google" in Settings to enable cloud save or leaderboards, the anonymous UID is upgraded (linked) to a permanent Google account; we receive your Google display name only. We do NOT receive your Google email address, profile photo, contacts, Google Drive contents, or any other Google account data.

                    Cloud save data (Firestore — only when you enable cloud save or use leaderboards):
                    • Stored under document path: users/[your-firebase-uid]
                    • Contents: a single JSON snapshot of the same in-game state otherwise stored locally on your device (current level, daily-level progress, lives, coins/tickets, inventory and equipped items, tutorial flags, settings such as music/sound/vibration/theme, ad-removal purchase flags, leaderboard display name cache, country code, daily best records).
                    • Schema version and the UTC timestamp of the last save.
                    • App version that produced the save.

                    Leaderboard data (Firestore — only if you opt in to leaderboards):
                    • lb_stages/[your-firebase-uid]: highest level reached, your display name (from Google), and ISO 2-letter country code derived from your device locale.
                    • lb_daily/[dailyId]_[your-firebase-uid]: per-daily-puzzle best record (moves used and completion time in seconds), your display name, and country code.
                    • Country code is "ZZ" if your device locale cannot be determined.

                    Provided by you (optional):
                    • Customer support emails and the contents of those messages
                    • In-app purchase receipts (processed by Google Play)

                    Locally stored on your device (not transmitted to us unless you enable cloud save):
                    • Game progress, coin balance, settings

                    자동 수집 정보 (광고/분석):
                    • 광고 식별자(AAID), 기기 정보, IP 주소, 충돌 로그, 게임 플레이 이벤트
                    • 결제 영수증은 Google Play를 통해 처리됩니다.

                    Firebase Authentication 식별자 (첫 실행 시 자동 발급, 클라우드 세이브 부트스트랩 용도):
                    • Firebase UID (익명): Firebase에서 발급하는 무작위 앱 전용 식별자. Google 로그인 전까지 이름·이메일·기기 정보와 연결되지 않습니다.
                    • 설정에서 "Google 로그인"을 하실 경우, 익명 UID가 영구 Google 계정과 연결되며, 회사는 사용자의 Google "표시 이름(display name)"만 수신합니다. Google 이메일·프로필 사진·연락처·Drive 내용 등은 수집하지 않습니다.

                    클라우드 세이브 데이터 (Firestore — 클라우드 세이브/랭킹 사용 시):
                    • 저장 경로: users/[사용자 Firebase UID]
                    • 내용: 게임 상태(현재 레벨, 데일리 진행, 라이프, 코인/티켓, 인벤토리 및 장착 아이템, 튜토리얼 플래그, 음악/사운드/진동/테마 설정, 광고 제거 구매 여부, 랭킹 표시 이름 캐시, 국가 코드, 데일리 베스트 기록)의 JSON 스냅샷
                    • 스키마 버전, 마지막 저장 UTC 시각, 앱 버전

                    랭킹 데이터 (Firestore — 랭킹 참여 시):
                    • lb_stages/[UID]: 최고 도달 레벨, 표시 이름(Google), ISO 2자리 국가 코드(기기 로캘 기반)
                    • lb_daily/[dailyId]_[UID]: 데일리 퍼즐별 최고 기록(이동 횟수, 클리어 시간(초)), 표시 이름, 국가 코드
                    • 국가 코드를 추출할 수 없는 경우 "ZZ"로 저장됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. Purposes of Processing / 처리 목적</SectionTitle>
                <Paragraph>
                    • Provide and maintain the game and its features
                    • Provide optional cloud save so your progress survives device changes and reinstalls
                    • Operate global and country leaderboards (stages + daily challenges)
                    • Process in-app purchases via Google Play Billing
                    • Display advertisements (personalized or non-personalized, depending on your consent)
                    • Measure ad performance and prevent ad fraud
                    • Analyze gameplay to improve features and fix bugs
                    • Respond to customer support requests
                    • Comply with legal obligations (tax, accounting, lawful requests)
                    • Detect, prevent, and respond to security incidents and fraud (including leaderboard score manipulation)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. Advertising and Third-Party SDKs / 광고 및 제3자 SDK</SectionTitle>
                <Paragraph>
                    Arrow Quest is supported by advertisements served through Google AdMob (and its mediation partners). These SDKs may collect device identifiers and usage data to serve ads, measure performance, and prevent fraud.

                    Advertising partners:
                    • Google AdMob — see https://policies.google.com/privacy
                    • Google User Messaging Platform (UMP) — manages your consent in EEA/UK/Switzerland
                    • Google Play Billing — processes in-app purchases (https://policies.google.com/privacy)

                    Backend / account / leaderboard providers:
                    • Firebase Authentication (Google LLC / Google Ireland Limited) — issues an anonymous UID and, optionally, links it to a Google account. See https://firebase.google.com/support/privacy
                    • Cloud Firestore (Google LLC / Google Ireland Limited) — stores your cloud save document and leaderboard entries. See https://firebase.google.com/support/privacy
                    • Google Play Games Services (Android) — provides the OAuth credential used to link your account when you choose "Sign in with Google". See https://policies.google.com/privacy

                    Personalized ads (EEA/UK/Switzerland):
                    • Disabled by default; only enabled after you grant consent in the UMP form
                    • Manageable any time in Settings → Privacy → Manage Consent

                    Personalized ads (other regions, including US/Korea):
                    • Subject to your device-level "Limit Ad Tracking" or "Opt out of Ads Personalization" setting
                    • In the US, California residents may opt out as described in Section 10

                    광고 파트너 및 정책: 자세한 데이터 수집 항목은 위 링크에서 확인하세요.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. Data Retention / 보유 기간</SectionTitle>
                <Paragraph>
                    • Advertising identifiers and analytics events: up to 14 months (Google AdMob default), then aggregated or deleted.
                    • Crash logs: up to 90 days.
                    • Customer support correspondence: up to 3 years after last contact.
                    • Purchase records (legal obligation under Korean Electronic Commerce Act and tax law): 5 years.
                    • Consent records (GDPR accountability): up to 3 years after withdrawal.
                    • Cloud save document (users/[uid]): retained for as long as you keep the linked account. Deleted within 30 days of an account-deletion request, or automatically after 24 months of complete inactivity.
                    • Leaderboard entries (lb_stages, lb_daily): retained while you participate; deleted within 30 days of an account-deletion request. Daily leaderboard entries older than 90 days may be aggregated or removed.
                    • Firebase Authentication account (anonymous UID or Google-linked UID): retained until you request deletion; deleted within 30 days of request.

                    Game progress stored locally on your device is removed when you uninstall the app or clear app data.

                    보유 기간: 광고/분석 데이터 최대 14개월, 결제 기록 5년(전자상거래법·국세기본법), 동의 기록 3년, 클라우드 세이브·랭킹 데이터는 삭제 요청 시 30일 이내 삭제 / 24개월 무활동 시 자동 삭제.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. International Data Transfers / 국제 전송</SectionTitle>
                <Paragraph>
                    Our service providers (primarily Google) process data in the United States and other countries outside the EEA/UK and Korea. Specifically, Firebase Authentication and Cloud Firestore process your UID, cloud save document, and leaderboard entries on Google Cloud infrastructure that may be located in the United States and other regions.

                    Safeguards we rely on:
                    • EU-US Data Privacy Framework (DPF) for transfers to certified US providers including Google LLC.
                    • Standard Contractual Clauses (SCCs) approved by the European Commission, including the UK Addendum.
                    • Supplementary technical measures: TLS in transit, encryption at rest where applicable.

                    For Korean users, transfers to overseas processors are disclosed pursuant to Article 28-8 of PIPA. You may obtain details by contacting us.

                    국제 전송: 광고/분석 데이터와 Firebase Authentication·Cloud Firestore에 저장되는 클라우드 세이브·랭킹 데이터는 Google LLC(미국 등)에서 처리될 수 있으며, EU-US DPF, 표준계약조항(SCC), 한국 개인정보보호법 제28조의8에 따른 보호조치를 적용합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. Sharing of Information / 제3자 제공</SectionTitle>
                <Paragraph>
                    We do not sell your personal information. We share data only with:

                    • Service providers acting on our behalf (Google AdMob, Google Play, Firebase Authentication, Cloud Firestore, analytics processors) under data processing agreements.
                    • Other players, but only your chosen display name, country code, and score (level / moves / time) — and only to the extent shown on public leaderboards, when you opt in to leaderboards.
                    • Law enforcement or government authorities when legally required (court order, subpoena, statutory request).
                    • Parties to a corporate transaction (merger, acquisition, asset sale) with continued protection guarantees.

                    We do not engage in cross-context behavioral advertising as defined by CPRA without consent.

                    회사는 사용자의 개인정보를 판매하지 않으며, 위탁 처리자(Google, Firebase 등)에게만 데이터 처리 계약에 따라 제공됩니다. 랭킹 참여 시 표시 이름·국가 코드·점수는 공개 리더보드를 통해 다른 플레이어에게 노출됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. Your Rights (GDPR / UK GDPR) / EU·영국 사용자의 권리</SectionTitle>
                <Paragraph>
                    If you are in the EEA, UK, or Switzerland, you have the following rights:

                    • Right of access (Art. 15) — request a copy of your data
                    • Right to rectification (Art. 16) — correct inaccurate data
                    • Right to erasure / "right to be forgotten" (Art. 17)
                    • Right to restriction of processing (Art. 18)
                    • Right to data portability (Art. 20)
                    • Right to object to processing based on legitimate interests (Art. 21), including direct marketing
                    • Right not to be subject to solely automated decisions (Art. 22)
                    • Right to withdraw consent at any time
                    • Right to lodge a complaint with a supervisory authority (e.g., your national Data Protection Authority)

                    To exercise these rights, email mind-wellness@naver.com with your Android Advertising ID (Settings → Google → Ads) so we can locate your data. We respond within 30 days.

                    EU/영국 사용자는 위 권리를 행사할 수 있으며, 감독기관에 신고할 권리도 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. California Residents' Rights (CCPA/CPRA) / 캘리포니아 사용자</SectionTitle>
                <Paragraph>
                    California residents have the following rights:

                    • Right to know what personal information we collect, use, and disclose
                    • Right to delete personal information
                    • Right to correct inaccurate personal information
                    • Right to opt out of "sale" or "sharing" for cross-context behavioral advertising (we do not sell, but advertising identifiers may be considered "sharing" under CPRA — toggle in Settings → Privacy)
                    • Right to limit use of sensitive personal information
                    • Right to non-discrimination for exercising your rights

                    To exercise these rights, email mind-wellness@naver.com or use the "Do Not Sell or Share My Personal Information" link in Settings → Privacy.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. Korean Users / 한국 사용자</SectionTitle>
                <Paragraph>
                    한국 개인정보보호법(PIPA)에 따른 권리:

                    • 개인정보 열람, 정정, 삭제, 처리정지 요구권
                    • 손해배상청구권
                    • 동의 철회권

                    개인정보 보호 책임자:
                    • 성명: 윤우재
                    • 이메일: mind-wellness@naver.com

                    분쟁 조정 및 신고:
                    • 개인정보분쟁조정위원회: 1833-6972 (privacy.go.kr)
                    • 개인정보침해신고센터: 118 (privacy.kisa.or.kr)
                    • 대검찰청 사이버수사과: 1301 (spo.go.kr)
                    • 경찰청 사이버수사국: 182 (police.go.kr)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. Children's Privacy / 아동 개인정보</SectionTitle>
                <Paragraph>
                    Arrow Quest is rated for ages 13 and older. We do not knowingly collect personal information from children under 13 (under 16 in some EEA jurisdictions, in line with national derogations under GDPR Article 8).

                    If you believe a child has provided us personal data, contact mind-wellness@naver.com and we will delete it promptly.

                    For users between the minimum age and the age of majority, processing of personal data is performed under the legal authority of a parent or guardian where required by local law.

                    아동 보호: 본 게임은 만 13세 이상 권장이며, 만 14세 미만 한국 사용자에 대해서는 법정대리인 동의 없이는 개인정보를 수집하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. Data Security / 데이터 보안</SectionTitle>
                <Paragraph>
                    Technical measures: TLS 1.2+ for transit, encryption at rest where supported by our processors, access controls, secure software development lifecycle.

                    Organizational measures: data minimization, role-based access, processor due diligence, breach response procedures.

                    Breach notification: In the event of a personal data breach likely to result in a high risk to your rights, we will notify the relevant supervisory authority within 72 hours where required, and affected users without undue delay.

                    보안 사고 통지: GDPR 및 PIPA에 따라 72시간 이내 감독기관 및 사용자에게 통지합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. Cookies and Similar Technologies / 쿠키 및 유사 기술</SectionTitle>
                <Paragraph>
                    The game itself does not use HTTP cookies. However, embedded SDKs (Google AdMob) may use advertising identifiers and local storage equivalents to deliver and measure ads. You can reset or limit these via your device settings:

                    • Android: Settings → Google → Ads → Delete advertising ID / Opt out of Ads Personalization
                    • Or in Arrow Quest: Settings → Privacy → Manage Consent
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>15. Automated Decision-Making / 자동화된 의사결정</SectionTitle>
                <Paragraph>
                    We do not engage in automated decision-making that produces legal or similarly significant effects on you (GDPR Art. 22). Ad targeting performed by advertising partners is non-significant profiling and is subject to your consent in applicable regions.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>16. Changes to This Policy / 정책 변경</SectionTitle>
                <Paragraph>
                    We may update this Privacy Policy from time to time. Material changes will be announced in-app at least 30 days before they take effect. Minor clarifications take effect when posted. Your continued use after the effective date constitutes acceptance of the updated policy.

                    중요 변경사항은 시행 30일 전 앱 내 공지하며, 사용자가 동의하지 않을 경우 서비스 이용을 중단할 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>17. Contact / 연락처</SectionTitle>
                <Paragraph>
                    Data Protection Contact / 개인정보 보호 책임자:
                    • Name: 윤우재 (Yoon Woo-jae)
                    • Email: mind-wellness@naver.com
                    • Postal address: 경기도 의왕시 시청1로 11, Republic of Korea

                    EU Representative (Art. 27 GDPR): Not yet appointed. Will be designated before active marketing within the EU. EU users may contact us directly at the email above; we treat such requests with the same priority.

                    UK Representative: Not yet appointed under the equivalent UK GDPR provisions.

                    We aim to respond to all privacy requests within 30 days.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>Policy Information / 방침 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-05-18
                    • Last updated / 최종 수정일: 2026-05-26
                    • Effective / 시행일: 2026-05-26
                    • Version / 버전: 1.1
                    • Applicable laws / 적용 법규: GDPR, UK GDPR, CCPA/CPRA, PIPA (Korea), COPPA
                    • v1.1 changes / 1.1 변경사항: Firebase Authentication, Cloud Firestore 기반 클라우드 세이브 및 랭킹 기능 도입에 따른 수집·전송·보유 항목 추가.
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default ArrowQuestPrivacyPolicy;
